import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Form, Row, Col, Button, Alert } from 'react-bootstrap';
import { GoFileZip } from 'react-icons/go';
import { isArchivePath } from 'Utils/archivePath';
import {
  getServiceCapabilities,
  supportedExtensionSet,
  supportedTypeNamesForRole,
} from 'Secure/serviceCapabilitiesApi';
import { looksLikeGlobList } from '../datasourcesApi';
import ProdToggle from './ProdToggle';

// The user no longer picks a file type — the extension is the type. A file source is always
// created as Folder Search (FILE_GENERIC_FOLDER, defaulted in datasourcesApi.toRequestDto),
// which discovers everything it can under the root and skips what it can't. The supported set
// is fetched from the platform API (backed by service-registration.json) and used only to warn
// when the root path points at a single unsupported file.

// Archive/container extensions are valid folder-like roots (the planner walks into them), so a
// root path ending in one must NOT trigger the "not supported" warning. Mirrors the ARCHIVE
// bucket in the worker's FileNodeType, plus single-file compressors it peels.
const CONTAINER_EXTENSIONS = new Set(['zip', 'jar', 'war', 'ear', 'gz', 'tgz', 'tar', 'snappy', 'sz', '7z', 'rar']);

/**
 * Best-effort file extension of a root path, or null when the path is a folder. Returns null for:
 * a trailing-slash path (explicit directory), an inside-archive virtual path (…!/inner), a segment
 * with no extension, and version-like fragments ("v1.2" → "2") that are really folders — real
 * extensions are short and contain a letter. Used only to decide whether to warn.
 */
function fileExtensionOfPath(rootPath) {
  if (!rootPath) return null;
  const p = String(rootPath).trim();
  if (p === '' || p.endsWith('/') || p.includes('!/')) return null;
  const seg = p.substring(p.lastIndexOf('/') + 1);
  const dot = seg.lastIndexOf('.');
  if (dot <= 0) return null; // no dot, or a dotfile like ".bashrc"
  const ext = seg.substring(dot + 1).toLowerCase();
  if (!/^[a-z0-9]{1,5}$/.test(ext) || !/[a-z]/.test(ext)) return null;
  return ext;
}

/**
 * Last forward-slash segment of a path, used to suggest a friendly data source name
 * when the user hasn't typed one. Mirrors the server-side {@code lastPathSegment}
 * default applied by {@code findOrCreateFileDataSource} for legacy wizard saves.
 */
function deriveNameFromPath(rootPath) {
  if (!rootPath) return '';
  const trimmed = rootPath.endsWith('/') && rootPath.length > 1
    ? rootPath.slice(0, -1)
    : rootPath;
  const slash = trimmed.lastIndexOf('/');
  if (slash < 0) return trimmed;
  return trimmed.substring(slash + 1);
}

const FileSourceEditor = ({ connection, setConnection }) => {
  const { id } = useParams();
  const [showAdvanced, setShowAdvanced] = useState(
    !!(connection.endpointUrl || connection.connectionString)
  );
  // Track whether the user has explicitly typed a name. Only auto-fill from the
  // root path while the field is untouched — once the user edits, leave it alone.
  const [nameTouched, setNameTouched] = useState(!!connection.name);

  const update = (key, value) => setConnection({ ...connection, [key]: value });

  const updateRootPath = (next) => {
    const patch = { ...connection, rootPath: next };
    if (!id && !nameTouched && (!connection.name || connection.name === deriveNameFromPath(connection.rootPath))) {
      patch.name = deriveNameFromPath(next);
    }
    setConnection(patch);
  };

  // Supported discover file types (extension → name), fetched from the platform API. Empty until
  // loaded and on any failure — an empty set means "unknown, don't gate" (never a false warning).
  const [supportedExts, setSupportedExts] = useState(new Set());
  const [supportedTypeNames, setSupportedTypeNames] = useState([]);
  useEffect(() => {
    let cancelled = false;
    getServiceCapabilities().then((caps) => {
      if (cancelled) return;
      setSupportedExts(supportedExtensionSet(caps, 'discover'));
      setSupportedTypeNames(supportedTypeNamesForRole(caps, 'discover'));
    });
    return () => { cancelled = true; };
  }, []);

  const scheme = connection.vfsScheme || 'LOCAL';
  const isLocal = scheme === 'LOCAL';
  const isSftp = scheme === 'SFTP';
  const isS3 = scheme === 'S3';
  const isAzure = scheme === 'AZURE_BLOB';
  const isSharePoint = scheme === 'SHAREPOINT';

  // SharePoint auth method (client secret vs certificate). Derived from which proof the
  // saved row carries, with an explicit override once the user toggles — the toggle also
  // clears the other proof's fields so exactly one proof is ever sent (server enforces XOR).
  const [spAuthOverride, setSpAuthOverride] = useState(null);
  const spAuthMethod = spAuthOverride
    || (connection.certificatePem || connection.certificatePrivateKey ? 'certificate' : 'secret');
  const setSpAuthMethod = (method) => {
    setSpAuthOverride(method);
    if (method === 'secret') {
      setConnection({ ...connection, certificatePem: '', certificatePrivateKey: '' });
    } else {
      setConnection({ ...connection, clientSecret: '' });
    }
  };

  // Warn only when the root path points at a single file whose extension we don't sample (and
  // isn't an archive container). Folders always proceed — generic discovery skips what it can't
  // read. Non-blocking: save stays enabled (folder paths with dots would otherwise false-positive).
  // Advisory-only syntax check (JS ≠ Java regex, but close enough for these patterns).
  // The worker re-validates and returns a 422 on a genuinely bad pattern.
  //
  // A comma-separated glob list (`*.jar, *.war`) is accepted FIRST, because it is both the
  // seeded default and the thing users type unprompted — and as a regex it is invalid
  // ("nothing to repeat"), so without this check the field would reject its own default.
  const filterInvalid = (raw) => {
    const f = (raw || '').trim();
    if (!f || f === '*') return false;
    if (looksLikeGlobList(f)) return false;
    try {
      new RegExp(f); // eslint-disable-line no-new
      return false;
    } catch (e) {
      return true;
    }
  };
  const fileNameFilterInvalid = filterInvalid(connection.fileNameFilter);
  const fileNameExcludeFilterInvalid = filterInvalid(connection.fileNameExcludeFilter);

  const candidateExt = fileExtensionOfPath(connection.rootPath);
  const unsupportedExt = supportedExts.size > 0
    && candidateExt
    && !supportedExts.has(candidateExt)
    && !CONTAINER_EXTENSIONS.has(candidateExt)
    ? candidateExt
    : null;

  // Disable the dedicated account/container fields only when the rootPath ALREADY encodes
  // BOTH account and container (azblob://account/container/...), since the worker parses them
  // from the path. An account-only path (azblob://account/) must leave the fields enabled and
  // required so the user can supply the container — otherwise the source can't be saved or
  // scanned. Mirrors parseAzureBlob() server-side.
  const azureRootParts = (() => {
    if (!isAzure) return { account: '', container: '' };
    const rp = String(connection.rootPath || '').trim();
    if (!/^azblob:\/\//i.test(rp)) return { account: '', container: '' };
    const parts = rp.slice('azblob://'.length).split('/').filter(Boolean);
    return { account: parts[0] || '', container: parts[1] || '' };
  })();
  // Disable + treat-as-optional only when the path encodes BOTH account and container.
  const azureRootPathHasPrefix = !!(azureRootParts.account && azureRootParts.container);

  // The "Discover everything" opt-in only applies to Folder Search sources. New file
  // sources default to FILE_GENERIC_FOLDER (fileType unset until save), typed rows
  // (created from candidate prefill / dialogs) carry their own fileType and never fan out.
  const isGenericType = (connection.fileType || 'FILE_GENERIC_FOLDER') === 'FILE_GENERIC_FOLDER';

  const rootPathPlaceholder = isS3
    ? 's3://my-bucket/data/'
    : isAzure
      ? 'azblob://account/container/prefix or /folder if account+container are set below'
      : isSharePoint
        ? 'sharepoint://contoso.sharepoint.com/sites/HR/Documents'
        : isSftp
          ? '/exports/crm-data'
          : '/exports/crm-data or /mnt/parquet/orders';

  return (
    <>
      <Row className='text-left mb-3'>
        <Col>
          <Form.Label>Data source name *</Form.Label>
          <Form.Control
            size='sm'
            type='text'
            placeholder='Sample data source'
            value={connection.name || ''}
            onChange={(e) => {
              setNameTouched(true);
              update('name', e.target.value);
            }}
            required
            isInvalid={!!connection.name && connection.name.includes(' ')}
          />
          <Form.Control.Feedback type='invalid'>
            Data source names must not include spaces.
          </Form.Control.Feedback>
        </Col>
      </Row>
      <ProdToggle />

      <Row className='text-left mb-3'>
        <Col>
          <Form.Group>
            <Form.Label>Root path *</Form.Label>
            <Form.Control
              size='sm'
              type='text'
              placeholder={rootPathPlaceholder}
              value={connection.rootPath || ''}
              onChange={(e) => updateRootPath(e.target.value)}
              required
            />
            <Form.Text className='text-muted'>
              Point at a folder to discover every supported file inside it and its subfolders —
              the file extension determines how each is scanned. You don't pick a file type.
            </Form.Text>
            {isArchivePath(connection.rootPath) && (
              <Form.Text className='text-muted d-flex align-items-center' style={{ gap: 6 }}>
                <GoFileZip aria-hidden='true' style={{ flexShrink: 0 }} />
                <span>
                  This path points inside an archive — the {'!/'} marker separates the
                  outer archive file from the folder inside it. Discovery materialises
                  the archive and walks the inner folder transparently.
                </span>
              </Form.Text>
            )}
            {unsupportedExt && (
              <Alert variant='warning' className='mt-2 mb-0 py-2 small' data-testid='file-type-unsupported'>
                <strong>.{unsupportedExt}</strong> files aren't supported for discovery.
                {supportedTypeNames.length > 0 && (
                  <> Supported types: {supportedTypeNames.join(', ')}.</>
                )}
                {' '}Point the root path at a folder to scan every supported file inside it.
              </Alert>
            )}
          </Form.Group>
        </Col>
      </Row>

      <Row className='text-left mb-3'>
        <Col>
          <Form.Group className='mb-0'>
            <Form.Check
              type='checkbox'
              id='file-source-recurse'
              data-testid='file-source-recurse'
              label='Discover sub folders'
              checked={connection.recurse !== false}
              onChange={(e) => update('recurse', e.target.checked)}
            />
            <Form.Text className='text-muted'>
              When on, files in the root folder and all of its sub folders are discovered.
            </Form.Text>
          </Form.Group>
        </Col>
      </Row>

      <Row className='text-left mb-3'>
        <Col xs={6}>
          <Form.Group>
            <Form.Label>Inclusion filter</Form.Label>
            <Form.Control
              size='sm'
              type='text'
              placeholder='* (all supported types)'
              data-testid='file-source-filename-filter'
              value={connection.fileNameFilter || ''}
              onChange={(e) => update('fileNameFilter', e.target.value)}
            />
            <Form.Text className='text-muted'>
              Only files matching this are discovered. A comma-separated list of patterns
              (<code>*.csv, *.xlsx</code>) or a regular expression. Matched against the file
              name, not the folder path, case-insensitive. Leave blank or * for all supported
              types.
            </Form.Text>
            {fileNameFilterInvalid && (
              <Alert variant='warning' className='mt-2 mb-0 py-2 small' data-testid='filename-filter-invalid'>
                This doesn't look like a pattern list or a valid regular expression.
              </Alert>
            )}
          </Form.Group>
        </Col>
        <Col xs={6}>
          <Form.Group>
            <Form.Label>Exclusion filter</Form.Label>
            <Form.Control
              size='sm'
              type='text'
              placeholder='e.g. *.jar, *.war, *.bak'
              data-testid='file-source-filename-exclude-filter'
              value={connection.fileNameExcludeFilter || ''}
              onChange={(e) => update('fileNameExcludeFilter', e.target.value)}
            />
            <Form.Text className='text-muted'>
              Files matching this are never opened or downloaded — the cheapest way to keep
              build output, backups and media out of a scan. A comma-separated list
              (<code>*.jar, *.war</code>) or a regular expression. Wins over the inclusion
              filter. Excluded files are reported by extension in the run's results.
              <br />
              Include a <code>/</code> to match the <strong>folder path</strong> instead of the
              file name — <code>**/build/**</code>, <code>**/node_modules/**</code>. Excluded
              folders are skipped entirely, so nothing inside them is even listed.
            </Form.Text>
            {fileNameExcludeFilterInvalid && (
              <Alert variant='warning' className='mt-2 mb-0 py-2 small'
                     data-testid='filename-exclude-filter-invalid'>
                This doesn't look like a pattern list or a valid regular expression.
              </Alert>
            )}
          </Form.Group>
        </Col>
      </Row>

      {isGenericType && (
        <Row className='text-left mb-3'>
          <Col>
            <Alert variant='warning' className='mb-0 py-2' data-testid='auto-discover-callout'>
              <Form.Check
                type='checkbox'
                id='file-source-auto-discover'
                data-testid='file-source-auto-discover'
                label={<strong>⚠ Discover everything — auto-create sub data sources</strong>}
                checked={connection.autoDiscoverCandidates === true}
                onChange={(e) => update('autoDiscoverCandidates', e.target.checked)}
              />
              <div className='small mt-1'>
                When a folder search finds structured folders (CSV, JSON, XML, Parquet, …), a
                sub data source is created automatically for each one and its own discovery run
                is launched after the folder search completes. Sub data sources appear in the
                Sub-sources panel on this page — not in the main Data Sources list — and runs
                are started a few at a time. You can cancel the remaining runs from the folder
                search's results page.
              </div>
            </Alert>
          </Col>
        </Row>
      )}

      {isSftp && (
        <>
          <Row className='text-left mb-3'>
            <Col xs={8}>
              <Form.Group>
                <Form.Label>SFTP host *</Form.Label>
                <Form.Control
                  size='sm'
                  type='text'
                  placeholder='sftp.example.com'
                  value={connection.vfsHost || ''}
                  onChange={(e) => update('vfsHost', e.target.value)}
                  required
                />
              </Form.Group>
            </Col>
            <Col xs={4}>
              <Form.Group>
                <Form.Label>Port</Form.Label>
                <Form.Control
                  size='sm'
                  type='number'
                  placeholder='22'
                  value={connection.vfsPort || ''}
                  onChange={(e) => update('vfsPort', e.target.value)}
                />
              </Form.Group>
            </Col>
          </Row>
          <Row className='text-left mb-3'>
            <Col xs={6}>
              <Form.Group>
                <Form.Label>Username *</Form.Label>
                <Form.Control
                  size='sm'
                  type='text'
                  autoComplete='off'
                  value={connection.vfsUsername || ''}
                  onChange={(e) => update('vfsUsername', e.target.value)}
                  required
                />
              </Form.Group>
            </Col>
            <Col xs={6}>
              <Form.Group>
                <Form.Label>Password</Form.Label>
                <Form.Control
                  size='sm'
                  type='password'
                  autoComplete='new-password'
                  value={connection.vfsPassword || ''}
                  onChange={(e) => update('vfsPassword', e.target.value)}
                />
              </Form.Group>
            </Col>
          </Row>
        </>
      )}

      {isS3 && (
        <>
          <Row className='text-left mb-3'>
            <Col xs={6}>
              <Form.Group>
                <Form.Label>AWS Region <span className='text-danger'>*</span></Form.Label>
                <Form.Control
                  size='sm'
                  type='text'
                  placeholder='us-east-1'
                  required
                  value={connection.region || ''}
                  onChange={(e) => update('region', e.target.value)}
                />
              </Form.Group>
            </Col>
          </Row>
          <Row className='text-left mb-3'>
            <Col xs={6}>
              <Form.Group>
                <Form.Label>Access Key ID</Form.Label>
                <Form.Control
                  size='sm'
                  type='text'
                  autoComplete='off'
                  value={connection.accessKeyId || ''}
                  onChange={(e) => update('accessKeyId', e.target.value)}
                />
                <Form.Text className='text-muted'>Leave blank to use IAM role</Form.Text>
              </Form.Group>
            </Col>
            <Col xs={6}>
              <Form.Group>
                <Form.Label>Secret Access Key</Form.Label>
                <Form.Control
                  size='sm'
                  type='password'
                  autoComplete='new-password'
                  value={connection.secretAccessKey || ''}
                  onChange={(e) => update('secretAccessKey', e.target.value)}
                />
                <Form.Text className='text-muted'>Leave blank to use IAM role</Form.Text>
              </Form.Group>
            </Col>
          </Row>
        </>
      )}

      {isAzure && (
        <>
          <Row className='text-left mb-3'>
            <Col xs={6}>
              <Form.Group>
                <Form.Label>
                  Storage Account{azureRootPathHasPrefix ? '' : ' *'}
                </Form.Label>
                <Form.Control
                  size='sm'
                  type='text'
                  value={connection.account || azureRootParts.account}
                  onChange={(e) => update('account', e.target.value)}
                  disabled={azureRootPathHasPrefix}
                />
              </Form.Group>
            </Col>
            <Col xs={6}>
              <Form.Group>
                <Form.Label>
                  Container{azureRootPathHasPrefix ? '' : ' *'}
                </Form.Label>
                <Form.Control
                  size='sm'
                  type='text'
                  value={connection.container || azureRootParts.container}
                  onChange={(e) => update('container', e.target.value)}
                  disabled={azureRootPathHasPrefix}
                />
              </Form.Group>
            </Col>
          </Row>
          <Row className='text-left mb-3'>
            <Col xs={6}>
              <Form.Group>
                <Form.Label>Account Key</Form.Label>
                <Form.Control
                  size='sm'
                  type='password'
                  autoComplete='new-password'
                  value={connection.accountKey || ''}
                  onChange={(e) => update('accountKey', e.target.value)}
                />
                <Form.Text className='text-muted'>Leave blank to use Managed Identity</Form.Text>
              </Form.Group>
            </Col>
            <Col xs={6}>
              <Form.Group>
                <Form.Label>SAS Token</Form.Label>
                <Form.Control
                  size='sm'
                  type='password'
                  autoComplete='new-password'
                  value={connection.sasToken || ''}
                  onChange={(e) => update('sasToken', e.target.value)}
                />
                <Form.Text className='text-muted'>
                  Alternative to account key. Connection string in advanced overrides both.
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>
        </>
      )}

      {isSharePoint && (
        <>
          <Row className='text-left mb-3'>
            <Col xs={6}>
              <Form.Group>
                <Form.Label>Tenant ID *</Form.Label>
                <Form.Control
                  size='sm'
                  type='text'
                  placeholder='00000000-0000-0000-0000-000000000000'
                  autoComplete='off'
                  value={connection.tenantId || ''}
                  onChange={(e) => update('tenantId', e.target.value)}
                  required
                />
                <Form.Text className='text-muted'>Entra ID directory (tenant) ID</Form.Text>
              </Form.Group>
            </Col>
            <Col xs={6}>
              <Form.Group>
                <Form.Label>Client ID *</Form.Label>
                <Form.Control
                  size='sm'
                  type='text'
                  placeholder='00000000-0000-0000-0000-000000000000'
                  autoComplete='off'
                  value={connection.clientId || ''}
                  onChange={(e) => update('clientId', e.target.value)}
                  required
                />
                <Form.Text className='text-muted'>App registration application (client) ID</Form.Text>
              </Form.Group>
            </Col>
          </Row>
          <Row className='text-left mb-2'>
            <Col>
              <Form.Label className='d-block mb-1'>Authentication</Form.Label>
              <Form.Check
                inline
                type='radio'
                id='sp-auth-secret'
                name='sp-auth-method'
                label='Client secret'
                checked={spAuthMethod === 'secret'}
                onChange={() => setSpAuthMethod('secret')}
              />
              <Form.Check
                inline
                type='radio'
                id='sp-auth-certificate'
                name='sp-auth-method'
                label='Certificate'
                checked={spAuthMethod === 'certificate'}
                onChange={() => setSpAuthMethod('certificate')}
              />
              <Form.Text className='text-muted d-block'>
                The app registration must be granted Sites.Read.All — or Sites.Selected plus a
                per-site read grant — with admin consent. See the SharePoint setup guide.
              </Form.Text>
            </Col>
          </Row>
          {spAuthMethod === 'secret' && (
            <Row className='text-left mb-3'>
              <Col xs={6}>
                <Form.Group>
                  <Form.Label>Client Secret{id ? '' : ' *'}</Form.Label>
                  <Form.Control
                    size='sm'
                    type='password'
                    autoComplete='new-password'
                    data-testid='sp-client-secret'
                    placeholder={id ? 'Leave blank to keep current; enter new secret to change' : ''}
                    value={connection.clientSecret || ''}
                    onChange={(e) => update('clientSecret', e.target.value)}
                    required={!id}
                  />
                  <Form.Text className='text-muted'>
                    Secret value from Certificates &amp; secrets (shown once at creation)
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
          )}
          {spAuthMethod === 'certificate' && (
            <Row className='text-left mb-3'>
              <Col xs={6}>
                <Form.Group>
                  <Form.Label>Certificate (PEM) *</Form.Label>
                  <Form.Control
                    as='textarea'
                    rows={4}
                    size='sm'
                    autoComplete='off'
                    placeholder={'-----BEGIN CERTIFICATE-----\n...'}
                    value={connection.certificatePem || ''}
                    onChange={(e) => update('certificatePem', e.target.value)}
                    required
                  />
                  <Form.Text className='text-muted'>
                    Public certificate uploaded to the app registration
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col xs={6}>
                <Form.Group>
                  <Form.Label>Private Key (PEM){id ? '' : ' *'}</Form.Label>
                  <Form.Control
                    as='textarea'
                    rows={4}
                    size='sm'
                    autoComplete='off'
                    data-testid='sp-private-key'
                    placeholder={id
                      ? 'Leave blank to keep current; paste a new key to change'
                      : '-----BEGIN PRIVATE KEY-----\n...'}
                    value={connection.certificatePrivateKey || ''}
                    onChange={(e) => update('certificatePrivateKey', e.target.value)}
                    required={!id}
                  />
                  <Form.Text className='text-muted'>
                    Stored encrypted; never shown again after save
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
          )}
        </>
      )}

      {(isS3 || isAzure || isSharePoint) && (
        <Row className='text-left mb-2'>
          <Col>
            <Button
              variant='link'
              size='sm'
              type='button'
              className='p-0'
              onClick={() => setShowAdvanced((v) => !v)}
            >
              {showAdvanced ? 'Hide advanced options' : 'Show advanced options'}
            </Button>
          </Col>
        </Row>
      )}

      {(isS3 || isAzure || isSharePoint) && showAdvanced && (
        <>
          {isAzure && (
            <Row className='text-left mb-3'>
              <Col>
                <Form.Group>
                  <Form.Label>Connection String</Form.Label>
                  <Form.Control
                    size='sm'
                    type='password'
                    autoComplete='new-password'
                    value={connection.connectionString || ''}
                    onChange={(e) => update('connectionString', e.target.value)}
                  />
                  <Form.Text className='text-muted'>Overrides account key + SAS token</Form.Text>
                </Form.Group>
              </Col>
            </Row>
          )}
          <Row className='text-left mb-3'>
            <Col>
              <Form.Group>
                <Form.Label>Endpoint URL</Form.Label>
                <Form.Control
                  size='sm'
                  type='text'
                  value={connection.endpointUrl || ''}
                  onChange={(e) => update('endpointUrl', e.target.value)}
                />
                <Form.Text className='text-muted'>
                  {isS3
                    ? 'Optional — for S3-compatible stores (MinIO, etc.)'
                    : isSharePoint
                      ? 'Optional — Microsoft Graph base URL override for national clouds'
                      : 'Optional — for Azurite local emulator'}
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>
        </>
      )}

      {isLocal && (
        <Row className='text-left mb-2'>
          <Col>
            <Form.Text className='text-muted'>
              Local paths must be readable by the discover worker container.
            </Form.Text>
          </Col>
        </Row>
      )}
    </>
  );
};

export default FileSourceEditor;
